import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Theme,
  Typography,
} from '@mui/material';
import { useContext, useMemo, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import useApplication from '@/hooks/useApplication';
import { ApplicationDataGridDetailsProps, ApplicationDataGridRowProps } from '@/models/Application';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';

export default function ApplicationDeleteModal({
  applicationDeleteModalOpen,
  setApplicationDeleteModalOpen,
  modalApplication,
  theme,
}: {
  applicationDeleteModalOpen: boolean;
  setApplicationDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalApplication: ApplicationDataGridDetailsProps;
  theme: Theme;
}) {
  const { setAlert } = useContext(SnackbarContext);
  const { deleteApplication } = useApplication();
  const [postingApplication, setPostingApplication] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const { allStatuses } = useContext(ConfigContext);

  const { localeLanguageCode } = useContext(LocaleContext);

  const {
    currentApps,
    reviewApps,
    historicApps,
    updateCurrentApps,
    updateReviewApps,
    updateHistoricApps,
  } = useContext(DashboardContext);

  const statusDescription = useMemo(() => {
    let desc = '';
    if (
      modalApplication != null &&
      modalApplication.status != null &&
      allStatuses != null &&
      localeLanguageCode != null
    ) {
      const s = allStatuses.find((st) => st.id == modalApplication.status);
      if (s != null && s.statusDescriptionTranslations != null) {
        desc =
          s.statusDescriptionTranslations[localeLanguageCode] != null
            ? s.statusDescriptionTranslations[localeLanguageCode]
            : '';
      }
    }

    return desc;
  }, [modalApplication, allStatuses, localeLanguageCode]);

  const updateApplicationsAll = (appId: number) => {
    const ca = [...currentApps];
    const i = ca.map((x) => x.applicationId).indexOf(appId);
    if (ca[i] != null) {
      const caf: ApplicationDataGridRowProps[] = ca.filter((c) => c.applicationId != appId);
      updateCurrentApps(caf);
    }

    const ra = [...reviewApps];
    const j = ra.map((x) => x.applicationId).indexOf(appId);
    if (ra[j] != null) {
      const raf: ApplicationDataGridRowProps[] = ra.filter((r) => r.applicationId != appId);
      updateReviewApps(raf);
    }

    const ha = [...historicApps];
    const k = ha.map((x) => x.applicationId).indexOf(appId);
    if (ha[k] != null) {
      const haf: ApplicationDataGridRowProps[] = ha.filter((h) => h.applicationId != appId);
      updateHistoricApps(haf);
    }
  };

  const postApplicationDelete = async () => {
    const appId = modalApplication.applicationId;
    if (appId != null) {
      setPostingApplication(true);
      const result = await deleteApplication(appId);
      if (result?.status != null && result.status.toLowerCase() == 'success') {
        updateApplicationsAll(appId);

        setPostingApplication(false);

        setAlert({
          severity: 'success',
          message: result.message,
        });

        setApplicationDeleteModalOpen(false);
      } else {
        setPostingApplication(false);
        setAlert({
          severity: 'error',
          message: `Error deleting the application with id: ${appId} \n${result.error}`,
        });
      }
    }
  };

  const handleContinue = async () => {
    setShowWarning(false);
    await postApplicationDelete();
  };

  const handleClose = () => {
    setShowWarning(false);
    setPostingApplication(false);
    setApplicationDeleteModalOpen(false);
  };

  return (
    <>
      <Dialog open={applicationDeleteModalOpen} onClose={handleClose}>
        <DialogTitleWithClose onClose={handleClose}>Delete Application</DialogTitleWithClose>
        <DialogContent>
          <Box>
            <Typography variant='overline2'>Application ID</Typography>
            <Typography color='textSecondary'>{modalApplication?.applicationId}</Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Program (Cycle)</Typography>
            <Typography color='textSecondary'>
              {`${modalApplication?.applicationType} (${modalApplication?.applicationCycle})`}
            </Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Applicant</Typography>
            <Typography color='textSecondary'>{modalApplication?.partner}</Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Primary Contact</Typography>
            <Typography color='textSecondary'>{modalApplication?.fullName}</Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Application Status</Typography>
            <Typography color='textSecondary'>{statusDescription}</Typography>
          </Box>

          {showWarning && (
            <Alert severity='warning' style={{ marginTop: '20px' }}>
              <div>Are you sure you want to delete this application?</div>
              <div>
                This will delete the application and all associated responses. You cannot undo this
                action.
              </div>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!showWarning && (
            <>
              <Button variant='secondary' size='large' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='primary'
                size='large'
                onClick={() => {
                  setShowWarning(true);
                }}
                disabled={postingApplication}
              >
                {postingApplication ? (
                  <>
                    <span>Deleting ... </span>
                    <CircularProgress
                      style={{
                        color: theme.palette.grey[500],
                        marginLeft: '5px',
                      }}
                      size='16px'
                    />
                  </>
                ) : (
                  'Delete Application'
                )}
              </Button>
            </>
          )}
          {showWarning && (
            <>
              <Button variant='secondary' size='large' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='primary'
                size='large'
                disabled={postingApplication}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
